import * as React from 'react';

import { CardLoader } from '@blockworks/ui/legacy/loaders';
import { cn } from '@blockworks/ui/utils';
import Image from 'next/image';
import { Link } from '@blockworks/ui/legacy/link';

import ByLine from '@/components/pages/frontpage/components/feature/middle/by-line';
import type { ArticleType } from '@/types/article-type';

type NewsFeedArticleProps = {
    className?: string;
    article: ArticleType;
    ref?: React.ForwardedRef<HTMLDivElement>;
};

const NewsFeedArticle = (props: NewsFeedArticleProps) => {
    const { article, ref, className } = props;

    return (
        <>
            {article ? (
                <div
                    ref={ref}
                    className={cn('grid grid-cols-1 grid-rows-[168px_minmax(168px,_1fr)] h-full', className)}
                >
                    <div>
                        <Link href={`/news/${article.slug}`} className="cursor-pointer">
                            {article.imageUrl ? (
                                <>
                                    <div className="hidden lg:block overflow-hidden h-full">
                                        <Image
                                            src={article.imageUrl}
                                            width={316}
                                            height={168}
                                            sizes="100vw"
                                            className="object-cover w-full h-full"
                                            alt="article-image"
                                        />
                                    </div>
                                    <div className="hidden md:block lg:hidden h-full">
                                        <Image
                                            src={article.imageUrl}
                                            width={600}
                                            height={168}
                                            sizes="100vw"
                                            className="object-cover w-full h-full"
                                            alt="article-image"
                                        />
                                    </div>
                                    <div className="block md:hidden h-full">
                                        <Image
                                            src={article.imageUrl}
                                            width={150}
                                            height={257}
                                            sizes="100vw"
                                            className="object-cover w-full h-full"
                                            alt="article-image"
                                        />
                                    </div>
                                </>
                            ) : (
                                <div className="animate-pulse h-full w-full">
                                    <div className="bg-slate-300 h-full w-full"></div>
                                </div>
                            )}
                        </Link>
                    </div>
                    <div>
                        <div className="flex gap-3 mt-3 mb-2">
                            <p className="text-xs text-left uppercase text-[#5637cd]">{article.primaryCategory}</p>

                            {article.categories.slice(1, 3).map(category => (
                                <p key={category} className="text-xs text-left uppercase text-gray-800">
                                    {category}
                                </p>
                            ))}
                        </div>
                        <div className="flex justify-start items-start self-stretch flex-grow-0 flex-shrink-0 relative gap-2.5 mb-3">
                            <Link
                                href={`/news/${article.slug}`}
                                className="font-headline flex-grow text-[18px] lg:text-[24px] font-semibold leading-snug hover:text-primary"
                            >
                                {article.title.length > 88 ? `${article.title.slice(0, 88)}..` : article.title}
                            </Link>
                        </div>
                        <div className="flex justify-start items-start self-stretch flex-grow-0 flex-shrink-0 relative gap-2.5 mb-3">
                            <p
                                className="flex-grow text-base text-left text-[#6a6a6a] text-light-gray"
                                dangerouslySetInnerHTML={{
                                    __html:
                                        article.excerpt.length > 96
                                            ? `${article.excerpt.slice(0, 96)}...`
                                            : article.excerpt,
                                }}
                            />
                        </div>
                        <ByLine
                            className="text-xxs text-[#6a6a6a]"
                            authors={article.authors}
                            publishedAt={article.publishedAt}
                        />
                    </div>
                </div>
            ) : (
                <CardLoader />
            )}
        </>
    );
};

export default NewsFeedArticle;
